import { useEffect, useState, useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Upload, Download, FileSpreadsheet, Phone, Mail, IndianRupee, ArrowRightCircle, Search, Building2, User } from "lucide-react";
import { toast } from "sonner";

const STATUSES = [
  { key: "new",         label: "New Lead",    accent: "#3B82F6", pill: "bg-blue-50 text-blue-700 border-blue-200" },
  { key: "contacted",   label: "Contacted",   accent: "#0EA5E9", pill: "bg-sky-50 text-sky-700 border-sky-200" },
  { key: "qualified",   label: "Qualified",   accent: "#6366F1", pill: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  { key: "meeting",     label: "Meeting",     accent: "#8B5CF6", pill: "bg-violet-50 text-violet-700 border-violet-200" },
  { key: "quotation",   label: "Quotation",   accent: "#F59E0B", pill: "bg-amber-50 text-amber-700 border-amber-200" },
  { key: "negotiation", label: "Negotiation", accent: "#F97316", pill: "bg-orange-50 text-orange-700 border-orange-200" },
  { key: "won",         label: "Won",         accent: "#10B981", pill: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  { key: "lost",        label: "Lost",        accent: "#F43F5E", pill: "bg-rose-50 text-rose-700 border-rose-200" },
];
const PRIORITIES = {
  high:   { label: "High",   pill: "bg-red-100 text-red-700" },
  medium: { label: "Medium", pill: "bg-yellow-100 text-yellow-800" },
  low:    { label: "Low",    pill: "bg-slate-100 text-slate-700" },
};
const SOURCES  = ["Website","Google","Referral","Walk-in","WhatsApp","Instagram","Facebook","Trade Show","Cold Call","Email","Other"];
const SERVICES = ["Architecture","Interior","Construction","Turnkey","Other"];

const empty = { name:"", company:"", phone:"", email:"", source:"Website", budget:0, priority:"medium",
                project_type:"Residential", service_required:"Interior", address:"", status:"new",
                assigned_to:"", follow_up_date:"", remarks:"" };

const SAMPLE_CSV = `name,company,phone,email,source,service_required,budget,priority,status,follow_up_date,remarks
Vikram Malhotra,Malhotra Interiors,+91 98100 10001,vikram@malhotra.in,Referral,Interior,8500000,high,new,2026-03-10,3BHK duplex
Aakash Realty,Aakash Realty,+91 98100 10003,contact@aakashrealty.in,Website,Architecture,120000000,high,qualified,2026-03-01,32-unit tower
Divya Menon,Menon Homes,+91 98100 10007,divya@menon.in,Referral,Interior,6800000,medium,won,,Full home makeover`;

function parseCSV(text) {
  const lines = text.replace(/\r/g,"").split("\n").filter(l => l.trim());
  if (!lines.length) return [];
  const parseLine = (line) => {
    const out = []; let cur = ""; let inQ = false;
    for (let i=0; i<line.length; i++) { const c = line[i];
      if (c==='"' && line[i+1]==='"') { cur+='"'; i++; }
      else if (c==='"') inQ = !inQ;
      else if (c===',' && !inQ) { out.push(cur); cur=""; }
      else cur += c;
    }
    out.push(cur); return out;
  };
  const header = parseLine(lines[0]).map(h => h.trim().toLowerCase());
  return lines.slice(1).map(line => {
    const cells = parseLine(line); const o = {};
    header.forEach((h,i) => o[h] = (cells[i]||"").trim()); return o;
  }).filter(r => r.name);
}

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [q, setQ] = useState("");
  const [fStatus, setFStatus] = useState("all");
  const [fSource, setFSource] = useState("all");
  const [fService, setFService] = useState("all");
  const [fPri, setFPri] = useState("all");
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);
  const dragRef = useRef(null);

  const load = async () => {
    const [l, d] = await Promise.all([api.get("/leads"), api.get("/leads/dashboard")]);
    setLeads(l.data); setDashboard(d.data);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => leads.filter(l => {
    if (q && !`${l.name} ${l.company||""} ${l.email||""} ${l.phone||""} ${l.lead_code||""}`.toLowerCase().includes(q.toLowerCase())) return false;
    if (fStatus !== "all" && l.status !== fStatus) return false;
    if (fSource !== "all" && l.source !== fSource) return false;
    if (fService !== "all" && l.service_required !== fService) return false;
    if (fPri !== "all" && l.priority !== fPri) return false;
    return true;
  }), [leads, q, fStatus, fSource, fService, fPri]);

  const openCreate = () => { setForm(empty); setEditingId(null); setModalOpen(true); };
  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    try {
      if (editingId) { await api.patch(`/leads/${editingId}`, form); toast.success("Lead updated"); }
      else { await api.post("/leads", form); toast.success("Lead created"); }
      setModalOpen(false); setForm(empty); setEditingId(null); load();
    } catch { toast.error("Save failed"); }
  };
  const moveStatus = async (lead, newStatus) => {
    if (lead.status === newStatus) return;
    try {
      setLeads(ls => ls.map(x => x.id === lead.id ? {...x, status: newStatus} : x));
      await api.post(`/leads/${lead.id}/status`, { status: newStatus });
      toast.success(`Moved to ${newStatus}`);
      load();
    } catch { toast.error("Update failed"); load(); }
  };
  const handleImport = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setImporting(true); setImportResult(null);
    try {
      const text = await file.text(); const items = parseCSV(text);
      if (!items.length) { toast.error("No rows"); setImporting(false); return; }
      const { data } = await api.post("/leads/bulk", { items });
      setImportResult(data);
      if (data.ok) toast.success(`Imported ${data.ok} leads`);
      if (data.fail) toast.error(`${data.fail} rows failed`);
      load();
    } catch (err) { toast.error("Import failed"); }
    finally { setImporting(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const downloadSample = () => {
    const url = URL.createObjectURL(new Blob([SAMPLE_CSV], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = "sample_leads.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  // Native HTML5 drag-and-drop
  const onDragStart = (lead) => (e) => { dragRef.current = lead; e.dataTransfer.effectAllowed = "move"; e.currentTarget.classList.add("opacity-40"); };
  const onDragEnd   = (e) => e.currentTarget.classList.remove("opacity-40");
  const onDragOver  = (e) => { e.preventDefault(); e.currentTarget.classList.add("ring-2","ring-primary/50"); };
  const onDragLeave = (e) => e.currentTarget.classList.remove("ring-2","ring-primary/50");
  const onDrop      = (status) => (e) => { e.preventDefault(); e.currentTarget.classList.remove("ring-2","ring-primary/50"); if (dragRef.current) { moveStatus(dragRef.current, status); dragRef.current = null; } };

  const d = dashboard || { total: 0, by_status: {}, won_value: 0, pipeline_value: 0, win_rate: 0, recent_activity: [] };
  const inr = (n) => Number(n||0).toLocaleString("en-IN");

  return (
    <div className="space-y-5" data-testid="leads-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground mb-1">Sales · CRM</div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold">Lead Management</h1>
          <div className="text-sm text-muted-foreground mt-1">Enquiry → Qualification → Meeting → Quotation → Won</div>
        </div>
        <div className="flex gap-2">
          <Dialog open={importOpen} onOpenChange={(o) => { setImportOpen(o); if (!o) setImportResult(null); }}>
            <DialogTrigger asChild><Button variant="outline" data-testid="import-leads-btn"><Upload className="w-4 h-4 mr-1.5" />Import CSV</Button></DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>Import Leads from CSV</DialogTitle></DialogHeader>
              <div className="p-4 rounded-lg bg-muted/50 text-xs">
                <div className="font-mono">name,company,phone,email,source,service_required,budget,priority,status,follow_up_date,remarks</div>
                <ul className="text-muted-foreground mt-2 space-y-1">
                  <li>• Only <strong>name</strong> is required</li>
                  <li>• <strong>status</strong>: new/contacted/qualified/meeting/quotation/negotiation/won/lost</li>
                  <li>• <strong>priority</strong>: low/medium/high · <strong>service_required</strong>: Architecture/Interior/Construction/Turnkey/Other</li>
                </ul>
                <Button variant="ghost" size="sm" onClick={downloadSample} className="text-primary p-0 h-auto mt-2"><Download className="w-3.5 h-3.5 mr-1" /> Sample CSV</Button>
              </div>
              <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={handleImport} className="block w-full text-sm file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:bg-primary file:text-primary-foreground hover:file:bg-primary/90" data-testid="csv-file-input" />
              {importing && <div className="text-sm text-muted-foreground flex items-center gap-2"><FileSpreadsheet className="w-4 h-4 animate-pulse" /> Importing…</div>}
              {importResult && <div className={`p-3 rounded-lg ${importResult.ok>0?"bg-emerald-50 border border-emerald-200":"bg-destructive/10 border border-destructive/30"}`}><div className="text-sm font-medium">✓ {importResult.ok} imported · ⚠ {importResult.fail} failed</div></div>}
            </DialogContent>
          </Dialog>
          <Button onClick={openCreate} data-testid="new-lead-btn" className="bg-primary text-primary-foreground"><Plus className="w-4 h-4 mr-1.5" /> New Lead</Button>
        </div>
      </div>

      {/* Dashboard cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3" data-testid="lead-dashboard">
        <MetricCard label="Total Leads" value={d.total} hint={`Pipeline ₹${inr(Math.round(d.pipeline_value/100000))}L`} tone="text-foreground" testid="metric-total" />
        <MetricCard label="New" value={d.by_status.new||0} hint="Fresh enquiries" tone="text-blue-600" testid="metric-new" />
        <MetricCard label="Qualified" value={d.by_status.qualified||0} hint="Ready for meeting" tone="text-indigo-600" testid="metric-qualified" />
        <MetricCard label="Quotation" value={(d.by_status.quotation||0)+(d.by_status.negotiation||0)} hint="In review" tone="text-amber-600" testid="metric-quotation" />
        <MetricCard label="Won" value={d.by_status.won||0} hint={`₹${inr(Math.round(d.won_value/100000))}L closed`} tone="text-emerald-600" testid="metric-won" />
        <MetricCard label="Lost" value={d.by_status.lost||0} hint={`${d.win_rate}% win rate`} tone="text-rose-600" testid="metric-lost" />
      </div>

      {/* Filter bar */}
      <div className="rounded-2xl p-3 bg-card border border-border/60 grid grid-cols-2 md:grid-cols-6 gap-2 text-sm" data-testid="lead-filters">
        <div className="col-span-2 relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
          <Input className="pl-8" placeholder="Search name, company, email, code…" value={q} onChange={e => setQ(e.target.value)} data-testid="lead-search" />
        </div>
        <Select value={fStatus} onValueChange={setFStatus}><SelectTrigger><SelectValue placeholder="All statuses" /></SelectTrigger><SelectContent><SelectItem value="all">All Statuses</SelectItem>{STATUSES.map(s => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent></Select>
        <Select value={fSource} onValueChange={setFSource}><SelectTrigger><SelectValue placeholder="Source" /></SelectTrigger><SelectContent><SelectItem value="all">All Sources</SelectItem>{SOURCES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
        <Select value={fService} onValueChange={setFService}><SelectTrigger><SelectValue placeholder="Service" /></SelectTrigger><SelectContent><SelectItem value="all">All Services</SelectItem>{SERVICES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
        <Select value={fPri} onValueChange={setFPri}><SelectTrigger><SelectValue placeholder="Priority" /></SelectTrigger><SelectContent><SelectItem value="all">All Priorities</SelectItem>{Object.entries(PRIORITIES).map(([k,m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}</SelectContent></Select>
      </div>

      {/* Kanban */}
      <div className="mb-2 flex items-center justify-between">
        <h2 className="font-display text-xl">Pipeline</h2>
        <div className="text-xs text-muted-foreground">Drag cards between columns to update status</div>
      </div>
      <div className="overflow-x-auto pb-2" data-testid="lead-kanban">
        <div className="flex gap-3 min-w-max">
          {STATUSES.map(col => {
            const items = filtered.filter(l => l.status === col.key);
            const sum = items.reduce((a,b) => a + Number(b.budget||0), 0);
            return (
              <div key={col.key} className="w-72 shrink-0 rounded-xl bg-muted/30 border border-border/50 p-3" data-testid={`kanban-col-${col.key}`}>
                <div className="flex items-center justify-between px-1 mb-3">
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full" style={{background: col.accent}} />
                    <span className="text-[11px] uppercase tracking-[0.15em] font-semibold">{col.label}</span>
                    <span className="text-xs px-1.5 py-0.5 rounded bg-background border border-border/60">{items.length}</span>
                  </div>
                  <div className="text-[10px] text-muted-foreground">₹{inr(Math.round(sum/100000))}L</div>
                </div>
                <div className="space-y-2 min-h-[80px] rounded-lg p-1 transition-colors"
                     onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop(col.key)}>
                  {items.map(l => { const pri = PRIORITIES[l.priority] || PRIORITIES.medium; return (
                    <div key={l.id} draggable onDragStart={onDragStart(l)} onDragEnd={onDragEnd}
                         className="kanban-card bg-card rounded-lg border border-border/50 p-3 cursor-grab hover:shadow-md transition-all"
                         data-testid={`lead-card-${l.id}`}>
                      <div className="flex items-start justify-between mb-1 gap-2">
                        <Link to={`/leads/${l.id}`} className="font-medium text-sm truncate hover:text-primary flex-1 min-w-0">{l.name}</Link>
                        <span className={`text-[9px] px-1.5 py-0.5 rounded font-medium ${pri.pill}`}>{pri.label}</span>
                      </div>
                      {l.company && <div className="text-[11px] text-muted-foreground truncate flex items-center gap-1"><Building2 className="w-2.5 h-2.5" />{l.company}</div>}
                      <div className="text-[10px] text-muted-foreground font-mono mt-0.5">{l.lead_code || "—"}</div>
                      <div className="mt-2 space-y-0.5 text-[11px] text-muted-foreground">
                        {l.phone && <div className="flex items-center gap-1.5"><Phone className="w-2.5 h-2.5" />{l.phone}</div>}
                        <div className="flex items-center gap-1.5"><IndianRupee className="w-2.5 h-2.5" />{inr(l.budget)}</div>
                        <div className="flex items-center gap-1.5 truncate">{l.service_required || "—"} · {l.source || "—"}</div>
                      </div>
                      <div className="mt-2.5 flex items-center justify-between border-t border-border/50 pt-2">
                        <div className="text-[10px] text-muted-foreground flex items-center gap-1"><User className="w-2.5 h-2.5" />{l.assigned_to ? "Assigned" : "Unassigned"}</div>
                        <Link to={`/leads/${l.id}`} className="p-1 rounded hover:bg-primary/10 text-primary" title="Open"><ArrowRightCircle className="w-4 h-4" /></Link>
                      </div>
                    </div>
                  );})}
                  {items.length === 0 && <div className="text-[11px] text-muted-foreground text-center py-6 opacity-60">Drop leads here</div>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Recent activity */}
      <div className="rounded-2xl p-5 bg-card border border-border/60" data-testid="lead-recent-activity">
        <h3 className="font-display text-lg mb-3">Recent Activity</h3>
        <div className="space-y-2">
          {(d.recent_activity || []).map((r,i) => (
            <div key={r.id || i} className="flex items-start gap-3 py-2 border-b border-border/40 last:border-0">
              <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center flex-shrink-0"><ActivityIcon type={r.activity_type} /></div>
              <div className="flex-1 min-w-0">
                <div className="text-sm"><Link to={`/leads/${r.lead_id}`} className="font-medium hover:text-primary">{r.lead_name || "Lead"}</Link> <span className="text-muted-foreground">· {r.description}</span></div>
                <div className="text-[11px] text-muted-foreground mt-0.5">{r.user_name || "System"} · {new Date(r.created_at).toLocaleString()}</div>
              </div>
            </div>
          ))}
          {(!d.recent_activity || !d.recent_activity.length) && <div className="text-sm text-muted-foreground py-3">No activity yet.</div>}
        </div>
      </div>

      {/* Create / Edit modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingId ? "Edit Lead" : "Add Lead"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Customer Name *</Label><Input data-testid="lead-name-input" value={form.name} onChange={e => setForm({...form, name: e.target.value})} /></div>
            <div><Label>Company</Label><Input value={form.company} onChange={e => setForm({...form, company: e.target.value})} /></div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} /></div>
            <div><Label>Email</Label><Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} /></div>
            <div className="col-span-2"><Label>Address</Label><Input value={form.address} onChange={e => setForm({...form, address: e.target.value})} /></div>
            <div><Label>Source</Label><Select value={form.source} onValueChange={v => setForm({...form, source: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{SOURCES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Service Required</Label><Select value={form.service_required} onValueChange={v => setForm({...form, service_required: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{SERVICES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Project Type</Label><Select value={form.project_type} onValueChange={v => setForm({...form, project_type: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{["Residential","Commercial","Hospitality","Retail","Office"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Budget (₹)</Label><Input type="number" value={form.budget} onChange={e => setForm({...form, budget: Number(e.target.value)})} /></div>
            <div><Label>Priority</Label><Select value={form.priority} onValueChange={v => setForm({...form, priority: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{Object.entries(PRIORITIES).map(([k,m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Status</Label><Select value={form.status} onValueChange={v => setForm({...form, status: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{STATUSES.map(s => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Follow-up date</Label><Input type="date" value={form.follow_up_date} onChange={e => setForm({...form, follow_up_date: e.target.value})} /></div>
            <div className="col-span-2"><Label>Notes / Remarks</Label><Textarea rows={2} value={form.remarks} onChange={e => setForm({...form, remarks: e.target.value})} /></div>
          </div>
          <div className="flex gap-2 mt-3">
            <Button variant="outline" className="flex-1" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button className="flex-1 bg-primary text-primary-foreground" onClick={save} data-testid="save-lead-btn">Save Lead</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MetricCard({ label, value, hint, tone, testid }) {
  return (
    <div className="rounded-2xl p-4 bg-card border border-border/60 hover:shadow-md hover:-translate-y-0.5 transition-all" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{label}</div>
      <div className={`font-display text-2xl mt-1 ${tone}`}>{value}</div>
      <div className="text-[11px] text-muted-foreground mt-1">{hint}</div>
    </div>
  );
}

function ActivityIcon({ type }) {
  const map = { created:"fa-plus", status_change:"fa-shuffle", note:"fa-note-sticky",
    meeting_scheduled:"fa-calendar-plus", meeting_done:"fa-calendar-check",
    document_upload:"fa-file-arrow-up", won:"fa-trophy", lost:"fa-xmark",
    call:"fa-phone", email:"fa-envelope", edit:"fa-pen", assignment:"fa-user-tag" };
  return <i className={`fa-solid ${map[type] || 'fa-circle-info'} text-xs text-muted-foreground`}></i>;
}
